import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaCalendarAlt, FaClock, FaSync } from 'react-icons/fa';
import './CalendarManager.css';
import API_BASE_URL from '../../config/api';

const dayTranslations = {
    'Monday': 'Seg',
    'Tuesday': 'Ter',
    'Wednesday': 'Qua',
    'Thursday': 'Qui',
    'Friday': 'Sex',
    'Saturday': 'Sáb',
    'Sunday': 'Dom'
};

const CalendarManager = ({ restrictedTeamId = null, onBack = null, isCoachDashboard = false, isCoordinator = false, coordinatorSportId = null }) => {
    const [activeTab, setActiveTab] = useState('events'); // 'events' or 'schedules'
    const [events, setEvents] = useState([]);
    const [sports, setSports] = useState([]);
    const [teams, setTeams] = useState([]);
    const [trainingSchedules, setTrainingSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEventModal, setShowEventModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [selectedSportFilter, setSelectedSportFilter] = useState('');
    const [selectedTeamFilter, setSelectedTeamFilter] = useState('');
    const [selectedEventTypeFilter, setSelectedEventTypeFilter] = useState('');
    const [editingEvent, setEditingEvent] = useState(null);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        eventType: 1,
        startDateTime: '',
        endDateTime: '',
        teamId: restrictedTeamId || null,
        sportId: (isCoordinator && coordinatorSportId) ? coordinatorSportId.toString() : '',
        location: '',
        description: '',
        opponentName: '',
        isHomeGame: true,
        hasTicketing: true,
        ticketPriceSocio: isCoachDashboard ? '3' : '',
        ticketPriceNonSocio: isCoachDashboard ? '7' : ''
    });

    const DAYS_ORDER = [
        { key: 'Monday', label: 'Segunda' },
        { key: 'Tuesday', label: 'Terça' },
        { key: 'Wednesday', label: 'Quarta' },
        { key: 'Thursday', label: 'Quinta' },
        { key: 'Friday', label: 'Sexta' },
        { key: 'Saturday', label: 'Sábado' },
        { key: 'Sunday', label: 'Domingo' },
    ];

    const [scheduleFormData, setScheduleFormData] = useState({
        teamId: '',
        daySchedules: [],   // [{ day, startTime, endTime }]
        location: '',
        validFrom: '',
        validUntil: '',
        isActive: true
    });
    const [copyFromDay, setCopyFromDay] = useState(null);
    const [copyTargetDays, setCopyTargetDays] = useState([]);

    // Derive the restricted sport from the team (when coach mode is active)
    const restrictedTeam = restrictedTeamId ? teams.find(t => t.id === restrictedTeamId) : null;
    const restrictedSportId = restrictedTeam?.sportId || null;

    // Pre-fill sport and team as soon as teams are loaded (coach or coordinator mode)
    useEffect(() => {
        if (restrictedTeamId && restrictedSportId) {
            setFormData(prev => ({
                ...prev,
                teamId: restrictedTeamId,
                sportId: restrictedSportId
            }));
            setScheduleFormData(prev => ({
                ...prev,
                teamId: restrictedTeamId.toString()
            }));
        } else if (isCoordinator && coordinatorSportId) {
            setFormData(prev => ({
                ...prev,
                sportId: coordinatorSportId.toString()
            }));
            setSelectedSportFilter(coordinatorSportId.toString());
        }
    }, [restrictedSportId, restrictedTeamId, isCoordinator, coordinatorSportId]);

    useEffect(() => {
        fetchEvents();
        fetchSports();
        fetchTeams();
        fetchTrainingSchedules();
    }, [isCoordinator, coordinatorSportId]);

    useEffect(() => {
        if (activeTab === 'events') {
            fetchEvents();
        } else if (activeTab === 'schedules') {
            fetchTrainingSchedules();
        }
    }, [activeTab]);

    const fetchEvents = async () => {
        try {
            const token = localStorage.getItem('token');
            let url = `${API_BASE_URL}/api/events`;
            if (restrictedTeamId) {
                url += `?teamId=${restrictedTeamId}`;
            }
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                // Filter out annual ticket virtual events as requested
                let filteredData = data.filter(e => e.title !== 'Bilhete Anual');
                if (isCoordinator && coordinatorSportId) {
                    filteredData = filteredData.filter(e => e.sportId === parseInt(coordinatorSportId));
                }
                setEvents(filteredData);
            }
        } catch (error) {
            console.error('Error fetching events:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSports = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/sports/all`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                let activeSports = data.filter(s => s.isActive);
                if (isCoordinator && coordinatorSportId) {
                    activeSports = activeSports.filter(s => s.id === parseInt(coordinatorSportId));
                }
                setSports(activeSports);
            }
        } catch (error) {
            console.error('Error fetching sports:', error);
        }
    };

    const fetchTeams = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/all`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                let filtered = data;
                if (isCoordinator && coordinatorSportId) {
                    filtered = filtered.filter(t => t.sportId === parseInt(coordinatorSportId));
                }
                setTeams(filtered);
            }
        } catch (error) {
            console.error('Error fetching teams:', error);
        }
    };

    const fetchTrainingSchedules = async () => {
        try {
            const token = localStorage.getItem('token');
            let url = `${API_BASE_URL}/api/trainingschedules`;
            if (restrictedTeamId) {
                url += `?teamId=${restrictedTeamId}`;
            }
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                let filteredSchedules = data;
                if (isCoordinator && coordinatorSportId) {
                    const coordSportIdNum = parseInt(coordinatorSportId);
                    filteredSchedules = filteredSchedules.filter(s => {
                        if (s.sportId) return s.sportId === coordSportIdNum;
                        if (s.teamId) {
                            const team = teams.find(t => t.id === s.teamId);
                            return team ? team.sportId === coordSportIdNum : true;
                        }
                        return true;
                    });
                }
                setTrainingSchedules(filteredSchedules);
            }
        } catch (error) {
            console.error('Error fetching training schedules:', error);
        }
    };

    const handleScheduleSubmit = async (e) => {
        e.preventDefault();
        if (scheduleFormData.daySchedules.length === 0) {
            alert('Seleciona pelo menos um dia de treino.');
            return;
        }
        if (!scheduleFormData.teamId) {
            alert('Por favor, selecione uma equipa.');
            return;
        }
        const token = localStorage.getItem('token');
        const dataToSend = { ...scheduleFormData, teamId: parseInt(scheduleFormData.teamId, 10) };
        try {
            const url = editingSchedule
                ? `${API_BASE_URL}/api/trainingschedules/${editingSchedule.id}`
                : `${API_BASE_URL}/api/trainingschedules`;
            const response = await fetch(url, {
                method: editingSchedule ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(dataToSend)
            });
            if (response.ok) {
                let scheduleId = editingSchedule?.id;
                if (response.status !== 204) {
                    const result = await response.json();
                    scheduleId = result.id;
                }
                await fetchTrainingSchedules();
                await handleGenerateEvents(scheduleId);
                setShowScheduleModal(false);
                resetScheduleForm();
            }
        } catch (error) {
            console.error('Error saving schedule:', error);
            alert('Erro ao guardar padrão');
        }
    };

    const handleScheduleDelete = async (id) => {
        if (!window.confirm('Tem certeza que deseja eliminar este padrão?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/trainingschedules/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                await fetchTrainingSchedules();
                alert('Padrão eliminado!');
            }
        } catch (error) {
            console.error('Error deleting schedule:', error);
            alert('Erro ao eliminar padrão');
        }
    };

    const handleCleanupOrphaned = async () => {
        if (!window.confirm('Isto irá remover todos os treinos que ficaram no calendário de padrões já eliminados. Continuar?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/trainingschedules/cleanup-orphaned`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const result = await response.json();
                alert(result.message);
                await fetchEvents();
            }
        } catch (error) {
            console.error('Error cleaning up orphaned sessions:', error);
            alert('Erro ao limpar treinos');
        }
    };

    const handleGenerateEvents = async (scheduleId) => {
        if (!window.confirm('Isto irá apagar todos os treinos existentes e gerar novos baseados no padrão. Continuar?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/trainingschedules/${scheduleId}/generate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const result = await response.json();
                await fetchEvents();
                alert(result.message);
            }
        } catch (error) {
            console.error('Error generating events:', error);
            alert('Erro ao gerar eventos');
        }
    };

    const handleEditSchedule = (schedule) => {
        setEditingSchedule(schedule);
        setScheduleFormData({
            teamId: schedule.teamId.toString(),
            daySchedules: schedule.daySchedules || [],
            location: schedule.location || '',
            validFrom: schedule.validFrom.split('T')[0],
            validUntil: schedule.validUntil.split('T')[0],
            isActive: schedule.isActive
        });
        setShowScheduleModal(true);
    };

    const resetScheduleForm = () => {
        setScheduleFormData({
            teamId: restrictedTeamId ? restrictedTeamId.toString() : '',
            daySchedules: [],
            location: '',
            validFrom: '',
            validUntil: '',
            isActive: true
        });
        setEditingSchedule(null);
        setCopyFromDay(null);
        setCopyTargetDays([]);
    };

    const toggleDay = (day) => {
        setScheduleFormData(prev => {
            const exists = prev.daySchedules.find(d => d.day === day);
            if (exists) {
                return { ...prev, daySchedules: prev.daySchedules.filter(d => d.day !== day) };
            }
            const last = prev.daySchedules[prev.daySchedules.length - 1];
            const newEntry = {
                day,
                startTime: last?.startTime || '18:00',
                endTime: last?.endTime || '20:00',
                location: last?.location || ''
            };
            const ordered = [...prev.daySchedules, newEntry].sort(
                (a, b) => DAYS_ORDER.findIndex(d => d.key === a.day) - DAYS_ORDER.findIndex(d => d.key === b.day)
            );
            return { ...prev, daySchedules: ordered };
        });
        setCopyFromDay(null);
        setCopyTargetDays([]);
    };

    const updateDayTime = (day, field, value) => {
        setScheduleFormData(prev => ({
            ...prev,
            daySchedules: prev.daySchedules.map(d => d.day === day ? { ...d, [field]: value } : d)
        }));
    };

    const applyTimeCopy = () => {
        const source = scheduleFormData.daySchedules.find(d => d.day === copyFromDay);
        if (!source || copyTargetDays.length === 0) return;
        setScheduleFormData(prev => ({
            ...prev,
            daySchedules: prev.daySchedules.map(d =>
                copyTargetDays.includes(d.day)
                    ? { ...d, startTime: source.startTime, endTime: source.endTime, location: source.location }
                    : d
            )
        }));
        setCopyFromDay(null);
        setCopyTargetDays([]);
    };

    const getEventColor = (eventType) => {
        switch (eventType) {
            case 1: return '#e74c3c'; // Game - Red
            case 2: return '#3498db'; // Training - Blue
            case 3: return '#2ecc71'; // Other - Green
            default: return '#95a5a6';
        }
    };
    const getEventPillClass = (eventType) => {
        switch (eventType) {
            case 1: return 'cm-event-pill--game';
            case 2: return 'cm-event-pill--training';
            case 3: return 'cm-event-pill--other';
            default: return 'cm-event-pill--default';
        }
    };

    const formatEventsForCalendar = () => {
        return events.filter(event => {
            // Apply filters
            if (isCoordinator && coordinatorSportId && event.sportId !== parseInt(coordinatorSportId)) return false;
            if (selectedSportFilter && event.sportId !== parseInt(selectedSportFilter)) return false;
            if (selectedTeamFilter && event.teamId !== parseInt(selectedTeamFilter)) return false;
            if (selectedEventTypeFilter && event.eventType !== parseInt(selectedEventTypeFilter)) return false;
            return true;
        }).map(event => {
            let displayTitle = event.title;
            if (event.eventType === 1) { // Game
                const opponent = event.opponentName || 'Adversário';
                displayTitle = event.isHomeGame ? `${event.title} - CD Póvoa vs ${opponent}` : `${event.title} - ${opponent} vs CD Póvoa`;
            }

            return {
                id: event.id,
                title: displayTitle,
                start: event.startDateTime,
                end: event.endDateTime,
                backgroundColor: getEventColor(event.eventType),
                borderColor: getEventColor(event.eventType),
                extendedProps: {
                    originalTitle: event.title,
                    eventType: event.eventType,
                    teamId: event.teamId,
                    sportId: event.sportId,
                    location: event.location,
                    description: event.description,
                    opponentName: event.opponentName,
                    isHomeGame: event.isHomeGame,
                    hasTicketing: event.hasTicketing,
                    ticketPriceSocio: event.ticketPriceSocio,
                    ticketPriceNonSocio: event.ticketPriceNonSocio
                }
            };
        });
    };

    const handleDateClick = (arg) => {
        resetForm();
        setFormData(prev => ({
            ...prev,
            startDateTime: arg.dateStr + 'T10:00',
            endDateTime: arg.dateStr + 'T12:00',
            ticketPriceSocio: isCoachDashboard ? '3' : '',
            ticketPriceNonSocio: isCoachDashboard ? '7' : ''
        }));
        setShowEventModal(true);
    };

    const handleEventClick = (clickInfo) => {
        const event = events.find(e => e.id === parseInt(clickInfo.event.id));
        if (event) {
            setEditingEvent(event);
            setFormData({
                title: event.title,
                eventType: event.eventType,
                startDateTime: event.startDateTime.slice(0, 16),
                endDateTime: event.endDateTime.slice(0, 16),
                teamId: event.teamId,
                sportId: event.sportId,
                location: event.location || '',
                description: event.description || '',
                opponentName: event.opponentName || '',
                isHomeGame: event.isHomeGame ?? true,
                hasTicketing: event.hasTicketing ?? true,
                ticketPriceSocio: event.ticketPriceSocio ?? '',
                ticketPriceNonSocio: event.ticketPriceNonSocio ?? ''
            });
            setShowEventModal(true);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const token = localStorage.getItem('token');
        const dataToSend = {
            ...formData,
            teamId: formData.teamId || null,
            sportId: parseInt(formData.sportId),
            eventType: parseInt(formData.eventType),
            hasTicketing: formData.hasTicketing,
            ticketPriceSocio: !formData.hasTicketing || formData.ticketPriceSocio === '' ? null : parseFloat(formData.ticketPriceSocio),
            ticketPriceNonSocio: !formData.hasTicketing || formData.ticketPriceNonSocio === '' ? null : parseFloat(formData.ticketPriceNonSocio)
        };

        try {
            const url = editingEvent
                ? `${API_BASE_URL}/api/events/${editingEvent.id}`
                : `${API_BASE_URL}/api/events`;

            const response = await fetch(url, {
                method: editingEvent ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(dataToSend)
            });

            if (response.ok) {
                await fetchEvents();
                setShowEventModal(false);
                resetForm();
                alert(editingEvent ? 'Evento atualizado!' : 'Evento criado!');
            }
        } catch (error) {
            console.error('Error saving event:', error);
            alert('Erro ao guardar evento');
        }
    };

    const handleDelete = async () => {
        if (!editingEvent || !window.confirm('Tem certeza que deseja eliminar este evento?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/events/${editingEvent.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                await fetchEvents();
                setShowEventModal(false);
                resetForm();
                alert('Evento eliminado!');
            }
        } catch (error) {
            console.error('Error deleting event:', error);
            alert('Erro ao eliminar evento');
        }
    };

    const resetForm = () => {
        const isGame = formData.eventType === 1;
        setFormData({
            title: '',
            eventType: 1,
            startDateTime: '',
            endDateTime: '',
            teamId: restrictedTeamId || null,
            sportId: (isCoordinator && coordinatorSportId) ? coordinatorSportId.toString() : (restrictedSportId || ''),
            location: '',
            description: '',
            opponentName: '',
            isHomeGame: true,
            hasTicketing: true,
            ticketPriceSocio: isCoachDashboard ? '3' : '',
            ticketPriceNonSocio: isCoachDashboard ? '7' : ''
        });
        setEditingEvent(null);
    };

    const getEventTypeName = (type) => {
        switch (type) {
            case 1: return 'Jogo';
            case 2: return 'Treino';
            case 3: return 'Outro';
            default: return '';
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            setShowEventModal(false);
            resetForm();
        }
    };

    if (loading) {
        return (
            <div className="calendar-manager">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>A carregar calendário...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="calendar-manager">
            <div className="manager-header">
                <div className="header-content">
                    <h1>Calendário de Eventos</h1>
                    <p className="header-subtitle">Gerir jogos, treinos e eventos</p>
                </div>
                <button
                    className="btn-add"
                    onClick={() => {
                        if (activeTab === 'events') {
                            resetForm();
                            setShowEventModal(true);
                        } else {
                            resetScheduleForm();
                            setShowScheduleModal(true);
                        }
                    }}
                >
                    <FaPlus /> {activeTab === 'events' ? 'Novo Evento' : 'Novo Treino'}
                </button>
            </div>

            {onBack && (
                <div style={{ marginBottom: '20px' }}>
                    <button
                        onClick={onBack}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#666',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            fontSize: '1rem',
                            padding: 0
                        }}
                    >
                        <FaArrowLeft /> Voltar ao Dashboard
                    </button>
                </div>
            )}

            {/* Tabs */}
            <div className="calendar-tabs">
                <button
                    className={`calendar-tab ${activeTab === 'events' ? 'active' : ''}`}
                    onClick={() => setActiveTab('events')}
                >
                    <FaCalendarAlt /> Calendário
                </button>
                <button
                    className={`calendar-tab ${activeTab === 'schedules' ? 'active' : ''}`}
                    onClick={() => setActiveTab('schedules')}
                >
                    <FaClock /> Treinos
                </button>
            </div>

            {/* Events Tab Content */}
            {activeTab === 'events' && (
                <>
                    {/* Filters — hide sport filter in restricted/coach mode */}
                    <div className="filter-section">
                        {!restrictedTeamId && !(isCoordinator && coordinatorSportId) && (
                            <div className="filter-group">
                                <label>Modalidade:</label>
                                <select value={selectedSportFilter} onChange={(e) => setSelectedSportFilter(e.target.value)}>
                                    <option value="">Todas</option>
                                    {sports.map(sport => (
                                        <option key={sport.id} value={sport.id}>{sport.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {(isCoordinator && coordinatorSportId) && (
                            <div className="filter-group">
                                <label>Modalidade:</label>
                                <input
                                    type="text"
                                    value={sports.find(s => s.id === parseInt(coordinatorSportId))?.name || 'Modalidade'}
                                    disabled
                                    className="filter-input-disabled"
                                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: '#f3f4f6' }}
                                />
                            </div>
                        )}

                        <div className="filter-group">
                            <label>Equipa:</label>
                            {restrictedTeamId ? (
                                <input
                                    type="text"
                                    value={teams.find(t => t.id === restrictedTeamId)?.name || 'Carregando...'}
                                    disabled
                                    className="filter-input-disabled"
                                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: '#f3f4f6' }}
                                />
                            ) : (
                                <select value={selectedTeamFilter} onChange={(e) => setSelectedTeamFilter(e.target.value)}>
                                    <option value="">Todas</option>
                                    {teams.map(team => (
                                        <option key={team.id} value={team.id}>{team.sportName} - {team.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className="filter-group">
                            <label>Tipo:</label>
                            <select value={selectedEventTypeFilter} onChange={(e) => setSelectedEventTypeFilter(e.target.value)}>
                                <option value="">Todos</option>
                                <option value="1">Jogos</option>
                                <option value="2">Treinos</option>
                                <option value="3">Outros</option>
                            </select>
                        </div>
                    </div>

                    {/* Calendar */}
                    <div className="calendar-container">
                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                            initialView="dayGridMonth"
                            headerToolbar={{
                                left: 'prev,next today',
                                center: 'title',
                                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
                            }}
                            events={formatEventsForCalendar()}
                            dateClick={handleDateClick}
                            eventClick={handleEventClick}
                            editable={true}
                            selectable={true}
                            selectMirror={true}
                            dayMaxEvents={true}
                            slotEventOverlap={false}
                            locale="pt"
                            buttonText={{
                                today: 'Hoje',
                                month: 'Mês',
                                week: 'Semana',
                                day: 'Dia',
                                list: 'Lista'
                            }}
                            eventDisplay="block"
                            eventTimeFormat={{
                                hour: '2-digit',
                                minute: '2-digit',
                                meridiem: false,
                                hour12: false
                            }}
                        />
                    </div>
                </>
            )}

            {/* Training Schedules Tab Content */}
            {activeTab === 'schedules' && (
                <div className="schedules-section">
                    {trainingSchedules.length === 0 ? (
                        <div className="empty-state">
                            <FaClock className="empty-icon" />
                            <h3>Nenhum padrão de treino definido</h3>
                            <p>Crie padrões de treino recorrentes para gerar eventos automaticamente</p>
                        </div>
                    ) : (
                        <div className="schedules-grid">
                            {trainingSchedules.map(schedule => (
                                <div key={schedule.id} className="schedule-card">
                                    <div className="schedule-header">
                                        <div>
                                            <h3>{schedule.teamName}</h3>
                                            <span className="sport-badge">{schedule.sportName}</span>
                                        </div>
                                        <div className="schedule-actions">
                                            <button
                                                className="btn-icon btn-generate"
                                                onClick={() => handleGenerateEvents(schedule.id)}
                                                title="Gerar Treinos"
                                            >
                                                <FaSync />
                                            </button>
                                            <button
                                                className="btn-icon btn-edit"
                                                onClick={() => handleEditSchedule(schedule)}
                                                title="Editar"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="btn-icon btn-delete"
                                                onClick={() => handleScheduleDelete(schedule.id)}
                                                title="Eliminar"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="schedule-body">
                                        <div className="schedule-days-list">
                                            {schedule.daySchedules?.map((ds, idx) => (
                                                <div key={idx} className="schedule-day-item">
                                                    <span className="day-name">{dayTranslations[ds.day] || ds.day}</span>
                                                    <span className="day-time">
                                                        {ds.startTime} - {ds.endTime}
                                                        {ds.location && <span className="day-location" style={{ marginLeft: '8px', fontSize: '0.85em', color: '#666' }}>📍 {ds.location}</span>}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        {schedule.location && (
                                            <div className="schedule-location">
                                                📍 {schedule.location}
                                            </div>
                                        )}
                                        <div className="schedule-period">
                                            {new Date(schedule.validFrom).toLocaleDateString()} - {new Date(schedule.validUntil).toLocaleDateString()}
                                        </div>
                                    </div>
                                    {!schedule.isActive && (
                                        <div className="schedule-inactive-badge">Inativo</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Event Modal */}
            {showEventModal && (
                <div className="cm-modal-overlay" onMouseDown={handleOverlayClick}>
                    <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className={`cm-modal-header cm-modal-header--${formData.eventType === 1 ? 'game' : formData.eventType === 2 ? 'training' : 'other'}`}>
                            <div className="cm-modal-header-text">
                                <span className="cm-modal-badge">
                                    {formData.eventType === 1 ? '⚽ Jogo' : formData.eventType === 2 ? '🏃 Treino' : '📌 Outro'}
                                </span>
                                <h2>{editingEvent ? 'Editar Evento' : 'Novo Evento'}</h2>
                            </div>
                            <button className="cm-modal-close" onClick={() => { setShowEventModal(false); resetForm(); }}>×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="cm-modal-form">
                            <div className="cm-form-field">
                                <label className="cm-form-label">Competição *</label>
                                <input
                                    type="text"
                                    className="cm-form-input"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="Ex: Liga, Taça..."
                                    required
                                />
                            </div>

                            <div className="cm-form-field">
                                <label className="cm-form-label">Tipo de Evento *</label>
                                <div className="cm-event-type-pills">
                                    {[{ v: 1, label: '⚽ Jogo' }, { v: 2, label: '🏃 Treino' }, { v: 3, label: '📌 Outro' }].map(({ v, label }) => (
                                        <button
                                            key={v}
                                            type="button"
                                            className={`cm-type-pill cm-type-pill--${v === 1 ? 'game' : v === 2 ? 'training' : 'other'} ${formData.eventType === v ? 'active' : ''}`}
                                            onClick={() => {
                                                const newPrices = (isCoachDashboard && v === 1)
                                                    ? { ticketPriceSocio: '3', ticketPriceNonSocio: '7' }
                                                    : { ticketPriceSocio: '', ticketPriceNonSocio: '' };

                                                setFormData({
                                                    ...formData,
                                                    eventType: v,
                                                    ...newPrices
                                                });
                                            }}
                                        >{label}</button>
                                    ))}
                                </div>
                            </div>

                            <div className="cm-form-row">
                                <div className="cm-form-field">
                                    <label className="cm-form-label">Início *</label>
                                    <input
                                        type="datetime-local"
                                        className="cm-form-input"
                                        value={formData.startDateTime}
                                        onChange={(e) => setFormData({ ...formData, startDateTime: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="cm-form-field">
                                    <label className="cm-form-label">Fim *</label>
                                    <input
                                        type="datetime-local"
                                        className="cm-form-input"
                                        value={formData.endDateTime}
                                        onChange={(e) => setFormData({ ...formData, endDateTime: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="cm-form-row">
                                {!(formData.teamId === null && formData.eventType === 3) && (
                                    <div className="cm-form-field">
                                        <label className="cm-form-label">Modalidade *</label>
                                        {restrictedTeamId ? (
                                            <input
                                                type="text"
                                                className="cm-form-input cm-form-input--disabled"
                                                value={sports.find(s => s.id === restrictedSportId)?.name || restrictedTeam?.sportName || ''}
                                                disabled
                                            />
                                        ) : (isCoordinator && coordinatorSportId) ? (
                                            <input
                                                type="text"
                                                className="cm-form-input cm-form-input--disabled"
                                                value={sports.find(s => s.id === parseInt(coordinatorSportId))?.name || 'Modalidade'}
                                                disabled
                                            />
                                        ) : (
                                            <select
                                                className="cm-form-input"
                                                value={formData.sportId}
                                                onChange={(e) => setFormData({ ...formData, sportId: e.target.value })}
                                                required={!(formData.teamId === null && formData.eventType === 3)}
                                            >
                                                <option value="">Selecione</option>
                                                {sports.map(sport => (
                                                    <option key={sport.id} value={sport.id}>{sport.name}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                )}
                                <div className="cm-form-field">
                                    <label className="cm-form-label">Equipa</label>
                                    {restrictedTeamId ? (
                                        <input
                                            type="text"
                                            className="cm-form-input cm-form-input--disabled"
                                            value={teams.find(t => t.id === restrictedTeamId)?.name || ''}
                                            disabled
                                        />
                                    ) : (
                                        <select
                                            className="cm-form-input"
                                            value={formData.teamId || ''}
                                            onChange={(e) => setFormData({ ...formData, teamId: e.target.value ? parseInt(e.target.value) : null })}
                                        >
                                            <option value="">Evento Geral</option>
                                            {teams.map(team => (
                                                <option key={team.id} value={team.id}>{team.sportName} - {team.name}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            </div>

                            <div className="cm-form-field">
                                <label className="cm-form-label">Local</label>
                                <input
                                    type="text"
                                    className="cm-form-input"
                                    value={formData.location}
                                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                />
                            </div>

                            {formData.eventType === 1 && (
                                <>
                                    <div className="cm-form-row">
                                        <div className="cm-form-field">
                                            <label className="cm-form-label">Adversário</label>
                                            <input
                                                type="text"
                                                className="cm-form-input"
                                                value={formData.opponentName}
                                                onChange={(e) => setFormData({ ...formData, opponentName: e.target.value })}
                                                placeholder="Nome do adversário"
                                            />
                                        </div>
                                        <div className="cm-form-field cm-form-field--toggle">
                                            <label className="cm-toggle-label">
                                                <input
                                                    type="checkbox"
                                                    className="cm-toggle-input"
                                                    checked={formData.isHomeGame}
                                                    onChange={(e) => setFormData({ ...formData, isHomeGame: e.target.checked })}
                                                />
                                                <span className="cm-toggle-track"><span className="cm-toggle-thumb" /></span>
                                                <span className="cm-toggle-text">Jogo em Casa</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="cm-form-row">
                                        <div className="cm-form-field cm-form-field--toggle">
                                            <label className="cm-toggle-label">
                                                <input
                                                    type="checkbox"
                                                    className="cm-toggle-input"
                                                    checked={formData.hasTicketing}
                                                    onChange={(e) => setFormData({ ...formData, hasTicketing: e.target.checked })}
                                                />
                                                <span className="cm-toggle-track"><span className="cm-toggle-thumb" /></span>
                                                <span className="cm-toggle-text">Bilheteira Disponível</span>
                                            </label>
                                        </div>
                                    </div>

                                    {formData.hasTicketing && (
                                        <div className="cm-form-row">
                                            <div className="cm-form-field">
                                                <label className="cm-form-label">Preço Bilhete (Sócio)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    className="cm-form-input"
                                                    value={formData.ticketPriceSocio}
                                                    onChange={(e) => setFormData({ ...formData, ticketPriceSocio: e.target.value })}
                                                    placeholder="€ 0.00"
                                                    disabled={isCoachDashboard}
                                                />
                                            </div>
                                            <div className="cm-form-field">
                                                <label className="cm-form-label">Preço Bilhete (Não Sócio)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    className="cm-form-input"
                                                    value={formData.ticketPriceNonSocio}
                                                    onChange={(e) => setFormData({ ...formData, ticketPriceNonSocio: e.target.value })}
                                                    placeholder="€ 0.00"
                                                    disabled={isCoachDashboard}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            <div className="cm-form-field">
                                <label className="cm-form-label">Descrição</label>
                                <textarea
                                    className="cm-form-input cm-form-textarea"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows="3"
                                    placeholder="Notas adicionais..."
                                />
                            </div>

                            <div className="cm-modal-actions">
                                {editingEvent && (
                                    <button type="button" className="cm-btn cm-btn--danger" onClick={handleDelete}>
                                        <FaTrash /> Eliminar
                                    </button>
                                )}
                                <button type="button" className="cm-btn cm-btn--ghost" onClick={() => { setShowEventModal(false); resetForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="cm-btn cm-btn--primary">
                                    {editingEvent ? 'Atualizar' : 'Criar'} Evento
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Training Schedule Modal */}
            {showScheduleModal && (
                <div className="cm-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowScheduleModal(false); resetScheduleForm(); } }}>
                    <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="cm-modal-header cm-modal-header--training">
                            <div className="cm-modal-header-text">
                                <span className="cm-modal-badge">🏃 Treinos</span>
                                <h2>{editingSchedule ? 'Editar Padrão' : 'Novo Padrão de Treino'}</h2>
                            </div>
                            <button className="cm-modal-close" onClick={() => { setShowScheduleModal(false); resetScheduleForm(); }}>×</button>
                        </div>

                        <form onSubmit={handleScheduleSubmit} className="cm-modal-form">
                            <div className="cm-form-field">
                                <label className="cm-form-label">Equipa *</label>
                                {restrictedTeamId ? (
                                    <input
                                        type="text"
                                        className="cm-form-input cm-form-input--disabled"
                                        value={teams.find(t => t.id === restrictedTeamId)?.name || ''}
                                        disabled
                                    />
                                ) : (
                                    <select
                                        className="cm-form-input"
                                        value={scheduleFormData.teamId}
                                        onChange={(e) => setScheduleFormData({ ...scheduleFormData, teamId: e.target.value })}
                                        required
                                    >
                                        <option value="">Selecione a equipa</option>
                                        {teams.map(team => (
                                            <option key={team.id} value={team.id}>{team.sportName} - {team.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* ── Day-schedule builder ── */}
                            <div className="cm-form-field">
                                <label className="cm-form-label">Dias e Horários *</label>

                                {/* Tile selector */}
                                <div className="cm-day-tiles">
                                    {DAYS_ORDER.map(({ key, label }) => {
                                        const active = scheduleFormData.daySchedules.some(d => d.day === key);
                                        return (
                                            <button key={key} type="button"
                                                className={`cm-day-tile ${active ? 'active' : ''}`}
                                                onClick={() => toggleDay(key)}
                                            >
                                                {label.slice(0, 3)}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Active day rows */}
                                {scheduleFormData.daySchedules.length > 0 ? (
                                    <div className="cm-day-rows">
                                        {scheduleFormData.daySchedules.map((ds) => {
                                            const label = DAYS_ORDER.find(d => d.key === ds.day)?.label;
                                            const isCopying = copyFromDay === ds.day;
                                            const otherDays = scheduleFormData.daySchedules.filter(d => d.day !== ds.day);

                                            return (
                                                <div key={ds.day} className={`cm-day-row ${isCopying ? 'cm-day-row--copying' : ''}`}>
                                                    <div className="cm-day-row-main">
                                                        <span className="cm-day-row-name">{label}</span>

                                                        <div className="cm-day-row-times">
                                                            <input
                                                                type="time"
                                                                className="cm-time-inp"
                                                                value={ds.startTime}
                                                                onChange={(e) => updateDayTime(ds.day, 'startTime', e.target.value)}
                                                            />
                                                            <span className="cm-time-arrow">→</span>
                                                            <input
                                                                type="time"
                                                                className="cm-time-inp"
                                                                value={ds.endTime}
                                                                onChange={(e) => updateDayTime(ds.day, 'endTime', e.target.value)}
                                                            />
                                                        </div>

                                                        <div className="cm-day-row-location" style={{ marginLeft: '10px', flex: 1, minWidth: '120px' }}>
                                                            <input
                                                                type="text"
                                                                className="cm-time-inp"
                                                                style={{ width: '100%', textOverflow: 'ellipsis' }}
                                                                placeholder="Local..."
                                                                value={ds.location || ''}
                                                                onChange={(e) => updateDayTime(ds.day, 'location', e.target.value)}
                                                            />
                                                        </div>

                                                        <div className="cm-day-row-actions">
                                                            {otherDays.length > 0 && (
                                                                <button
                                                                    type="button"
                                                                    className={`cm-copy-btn ${isCopying ? 'active' : ''}`}
                                                                    title="Copiar horário para outros dias"
                                                                    onClick={() => {
                                                                        setCopyFromDay(isCopying ? null : ds.day);
                                                                        setCopyTargetDays([]);
                                                                    }}
                                                                >
                                                                    Copiar
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="cm-remove-day-btn"
                                                                title="Remover dia"
                                                                onClick={() => toggleDay(ds.day)}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Copy panel */}
                                                    {isCopying && (
                                                        <div className="cm-copy-panel">
                                                            <span className="cm-copy-panel-label">
                                                                Aplicar {ds.startTime}–{ds.endTime} a:
                                                            </span>
                                                            <div className="cm-copy-targets">
                                                                {otherDays.map(other => {
                                                                    const otherLabel = DAYS_ORDER.find(d => d.key === other.day)?.label;
                                                                    const checked = copyTargetDays.includes(other.day);
                                                                    return (
                                                                        <label key={other.day} className={`cm-copy-target ${checked ? 'checked' : ''}`}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={checked}
                                                                                onChange={() =>
                                                                                    setCopyTargetDays(prev =>
                                                                                        checked ? prev.filter(d => d !== other.day) : [...prev, other.day]
                                                                                    )
                                                                                }
                                                                            />
                                                                            {otherLabel.slice(0, 3)}
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="cm-copy-apply-btn"
                                                                disabled={copyTargetDays.length === 0}
                                                                onClick={applyTimeCopy}
                                                            >
                                                                Aplicar
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <p className="cm-days-hint">Seleciona os dias de treino em cima</p>
                                )}
                            </div>

                            <div className="cm-form-field">
                                <label className="cm-form-label">Local Geral (Opcional)</label>
                                <input
                                    type="text"
                                    className="cm-form-input"
                                    placeholder="Local padrão se não especificado no dia"
                                    value={scheduleFormData.location}
                                    onChange={(e) => setScheduleFormData({ ...scheduleFormData, location: e.target.value })}
                                />
                            </div>

                            <div className="cm-form-row">
                                <div className="cm-form-field">
                                    <label className="cm-form-label">Válido De *</label>
                                    <input
                                        type="date"
                                        className="cm-form-input"
                                        value={scheduleFormData.validFrom}
                                        onChange={(e) => setScheduleFormData({ ...scheduleFormData, validFrom: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="cm-form-field">
                                    <label className="cm-form-label">Válido Até *</label>
                                    <input
                                        type="date"
                                        className="cm-form-input"
                                        value={scheduleFormData.validUntil}
                                        onChange={(e) => setScheduleFormData({ ...scheduleFormData, validUntil: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="cm-form-field cm-form-field--toggle">
                                <label className="cm-toggle-label">
                                    <input
                                        type="checkbox"
                                        className="cm-toggle-input"
                                        checked={scheduleFormData.isActive}
                                        onChange={(e) => setScheduleFormData({ ...scheduleFormData, isActive: e.target.checked })}
                                    />
                                    <span className="cm-toggle-track"><span className="cm-toggle-thumb" /></span>
                                    <span className="cm-toggle-text">Padrão Ativo</span>
                                </label>
                            </div>

                            <div className="cm-modal-actions">
                                <button type="button" className="cm-btn cm-btn--ghost" onClick={() => { setShowScheduleModal(false); resetScheduleForm(); }}>
                                    Cancelar
                                </button>
                                <button type="submit" className="cm-btn cm-btn--primary">
                                    {editingSchedule ? 'Atualizar' : 'Criar'} Padrão
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarManager;
